import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faFlag, faLink, faEyeSlash, faEye, faCheck, faShieldHalved, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import './Questions.css';

/* One conversation between a student and their teacher (student, teacher and admin pages).
   me: 'student' | 'teacher' | 'admin' · names: { student, teacher }
   onSend(body, link) -> Promise (students + teachers) · onReport(message) · onHide(message) / onClearReport(message) (admin) */
const when = (v) => {
  const d = v ? new Date(v) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

export const SafetyNote = ({ me }) => (
  <p className="rmq-safety">
    <FontAwesomeIcon icon={faShieldHalved} />
    <span>
      {me === 'teacher'
        ? 'Messages are visible to RoboMind and to the student’s parents. Keep it about classes and projects; phone numbers, emails and social media can’t be shared.'
        : 'Your parents and RoboMind can read these messages. Ask about your classes and projects. Phone numbers, emails and social media can’t be shared here.'}
    </span>
  </p>
);

const Conversation = ({ me, names = {}, messages = [], onSend, onReport, onHide, onClearReport, left, limit, placeholder, emptyText }) => {
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [showLink, setShowLink] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onSend(body.trim(), link.trim() || null);
      setBody('');
      setLink('');
      setShowLink(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Couldn’t send it. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const nameOf = (from) => (from === 'teacher' ? names.teacher || 'Teacher' : names.student || 'Student');
  const mine = (m) => (me === 'admin' ? m.from === 'teacher' : m.from === me);

  return (
    <div className="rmq-conv">
      <ul className="rmq-msgs" aria-live="polite">
        {messages.length === 0 && <li className="rmq-empty">{emptyText || 'No messages yet.'}</li>}
        {messages.map((m) => (
          <li key={m.id} className={`rmq-msg ${mine(m) ? 'is-mine' : ''} is-${m.from} ${m.hidden ? 'is-hidden' : ''} ${m.reported ? 'is-reported' : ''}`}>
            <div className="rmq-bubble">
              <span className="rmq-who">{nameOf(m.from)}</span>
              <p>{m.body}</p>
              {m.link && (
                <a href={m.link} target="_blank" rel="noopener noreferrer" className="rmq-link">
                  <FontAwesomeIcon icon={faLink} /> Open the project <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              )}
            </div>
            <div className="rmq-meta">
              <span>{when(m.at)}</span>
              {mine(m) && me !== 'admin' && m.read && (
                <span>
                  <FontAwesomeIcon icon={faCheck} /> Seen
                </span>
              )}
              {m.reported && <span className="rmq-flag">Reported{m.reportedBy ? ` by the ${m.reportedBy}` : ''}{m.reportNote ? `: “${m.reportNote}”` : ''}</span>}
              {m.hidden && <span className="rmq-flag">Hidden</span>}
              {onReport && !mine(m) && !m.reported && (
                <button type="button" onClick={() => onReport(m)} title="Report this message">
                  <FontAwesomeIcon icon={faFlag} /> Report
                </button>
              )}
              {onHide && (
                <button type="button" onClick={() => onHide(m)}>
                  <FontAwesomeIcon icon={m.hidden ? faEye : faEyeSlash} /> {m.hidden ? 'Show' : 'Hide'}
                </button>
              )}
              {onClearReport && m.reported && (
                <button type="button" onClick={() => onClearReport(m)}>
                  <FontAwesomeIcon icon={faCheck} /> Report checked
                </button>
              )}
            </div>
          </li>
        ))}
        <li ref={endRef} aria-hidden="true" />
      </ul>

      {onSend && (
        <form className="rmq-form" onSubmit={send}>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={800}
            rows={3}
            placeholder={placeholder || 'Write your message…'}
            disabled={busy || left === 0}
          />
          {showLink && (
            <input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://scratch.mit.edu/projects/…" disabled={busy} />
          )}
          {error && <p className="rmq-error">{error}</p>}
          <div className="rmq-form-bar">
            <button type="button" className="rmq-ghost" onClick={() => setShowLink((v) => !v)} disabled={busy}>
              <FontAwesomeIcon icon={faLink} /> {showLink ? 'No link' : 'Add a project link'}
            </button>
            {left != null && (
              <small className="rmq-left">{left === 0 ? `You sent ${limit} messages today. Try again tomorrow!` : `${left} ${left === 1 ? 'message' : 'messages'} left today`}</small>
            )}
            <button type="submit" className="rmq-send" disabled={busy || !body.trim() || left === 0}>
              <FontAwesomeIcon icon={faPaperPlane} /> {busy ? 'Sending…' : 'Send'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default Conversation;
